import {
  BATTERY_SUBTYPE,
  CHARGER_SUBTYPE,
  DOME_SUBTYPE,
  EAR_MOLD_SUBTYPE,
  OTHER_SUBTYPE,
  RECEIVER_SUBTYPE,
  REMOTE_SUBTYPE,
  REPAIR_SUBTYPE,
  SERVICE_PLAN_SUBTYPE,
  TUBE_SUBTYPE,
  WARRANTY_SUBTYPE
} from "constants/products";
import {
  ACCESSORY_PRODUCT_TYPE,
  FEE_PRODUCT_TYPE,
  HEARING_AID_PRODUCT_TYPE,
  SERVICE_PRODUCT_TYPE,
  SUPPLY_PRODUCT_TYPE
} from "constants/sales";
import { MasterProductInput } from "services/types/master-products.type";

import { AddMasterProductFormValues } from "./add-master-product-form.component";

/**
 * See products.util.ts for the full cpt_code/cpt_codes contract. Short version:
 * `cpt_code` is the primary FK, `cpt_codes` is the additional-only M2M list
 * (does NOT contain the primary).
 */
export const formatToMasterProductInput = (
  values: AddMasterProductFormValues,
  multipleCptCodesFeature?: boolean
): MasterProductInput => {
  const base = formatByType(values);
  if (!multipleCptCodesFeature) {
    // Defensively strip cpt_codes: formatByType doesn't include it today,
    // but this guard prevents leakage if a formatter adds it later.
    const { cpt_codes: _, ...rest } = base;
    return rest;
  }
  return { ...base, cpt_codes: values.cpt_codes?.map((c) => c.id) };
};

const formatByType = (
  values: AddMasterProductFormValues
): MasterProductInput => {
  const type = Number(values.type);
  const subtype = Number(values.subtype);
  if (type === HEARING_AID_PRODUCT_TYPE) return formatHearingAid(values);
  if (type === SERVICE_PRODUCT_TYPE && subtype === WARRANTY_SUBTYPE)
    return formatWarranty(values);
  if (type === SERVICE_PRODUCT_TYPE && subtype === REPAIR_SUBTYPE)
    return formatRepair(values);
  if (type === SERVICE_PRODUCT_TYPE && subtype === SERVICE_PLAN_SUBTYPE)
    return formatServicePlan(values);
  if (type === ACCESSORY_PRODUCT_TYPE && subtype === REMOTE_SUBTYPE)
    return formatRemote(values);
  if (type === ACCESSORY_PRODUCT_TYPE && subtype === BATTERY_SUBTYPE)
    return formatBattery(values);
  if (type === ACCESSORY_PRODUCT_TYPE && subtype === DOME_SUBTYPE)
    return formatDome(values);
  if (type === ACCESSORY_PRODUCT_TYPE && subtype === TUBE_SUBTYPE)
    return formatTube(values);
  if (type === ACCESSORY_PRODUCT_TYPE && subtype === CHARGER_SUBTYPE)
    return formatCharger(values);
  if (type === ACCESSORY_PRODUCT_TYPE && subtype === EAR_MOLD_SUBTYPE)
    return formatEarmold(values);
  if (type === ACCESSORY_PRODUCT_TYPE && subtype === RECEIVER_SUBTYPE)
    return formatReceiver(values);
  if (type === FEE_PRODUCT_TYPE) return formatFee(values);
  if (type === SUPPLY_PRODUCT_TYPE) return formatSupply(values);
  return formatOther(values);
};

const formatHearingAid = (
  values: AddMasterProductFormValues
): MasterProductInput => {
  const { technology, type } = values.specification ?? {};
  return {
    type: HEARING_AID_PRODUCT_TYPE,
    cpt_code: values?.cpt_code?.id,
    model: values.model,
    manufacturer: Number(values.manufacturer?.id),
    description: values.description,
    specification: {
      technology: Number(technology),
      type: Number(type),
      allowed_subtypes: []
    }
  };
};

const formatBattery = (
  values: AddMasterProductFormValues
): MasterProductInput => {
  const { type, color, warranty_months } = values.specification ?? {};
  return {
    type: ACCESSORY_PRODUCT_TYPE,
    subtype: BATTERY_SUBTYPE,
    model: values.model,
    cpt_code: values?.cpt_code?.id,
    manufacturer: Number(values.manufacturer?.id),
    description: values.description,
    specification: {
      type,
      color: color ? Number(color) : null,
      warranty_months: warranty_months ? warranty_months : undefined
    }
  };
};

const formatDome = (values: AddMasterProductFormValues): MasterProductInput => {
  return {
    type: ACCESSORY_PRODUCT_TYPE,
    subtype: DOME_SUBTYPE,
    model: values.model,
    cpt_code: values?.cpt_code?.id,
    manufacturer: Number(values.manufacturer?.id),
    description: values.description,
    specification: {}
  };
};

const formatTube = (values: AddMasterProductFormValues): MasterProductInput => {
  return {
    type: ACCESSORY_PRODUCT_TYPE,
    subtype: TUBE_SUBTYPE,
    model: values.model,
    cpt_code: values?.cpt_code?.id,
    manufacturer: Number(values.manufacturer?.id),
    description: values.description,
    specification: {}
  };
};

const formatReceiver = (
  values: AddMasterProductFormValues
): MasterProductInput => {
  return {
    type: ACCESSORY_PRODUCT_TYPE,
    subtype: RECEIVER_SUBTYPE,
    model: values.model,
    cpt_code: values?.cpt_code?.id,
    manufacturer: Number(values.manufacturer?.id),
    description: values.description
  };
};

const formatCharger = (
  values: AddMasterProductFormValues
): MasterProductInput => {
  return {
    type: ACCESSORY_PRODUCT_TYPE,
    subtype: CHARGER_SUBTYPE,
    model: values.model,
    cpt_code: values?.cpt_code?.id,
    manufacturer: Number(values.manufacturer?.id),
    description: values.description,
    specification: {}
  };
};

const formatEarmold = (
  values: AddMasterProductFormValues
): MasterProductInput => {
  return {
    type: ACCESSORY_PRODUCT_TYPE,
    subtype: EAR_MOLD_SUBTYPE,
    model: values.model,
    cpt_code: values?.cpt_code?.id,
    manufacturer: Number(values.manufacturer?.id),
    description: values.description
  };
};

const formatWarranty = (
  values: AddMasterProductFormValues
): MasterProductInput => {
  const { warranty_months, warranty_field } = values.specification ?? {};
  return {
    type: SERVICE_PRODUCT_TYPE,
    subtype: WARRANTY_SUBTYPE,
    cpt_code: values?.cpt_code?.id,
    description: values.description,
    specification: {
      warranty_months,
      warranty_field
    }
  };
};

const formatRepair = (
  values: AddMasterProductFormValues
): MasterProductInput => {
  return {
    type: SERVICE_PRODUCT_TYPE,
    subtype: REPAIR_SUBTYPE,
    cpt_code: values?.cpt_code?.id,
    description: values.description,
    specification: {}
  };
};

const formatServicePlan = (
  values: AddMasterProductFormValues
): MasterProductInput => {
  return {
    type: SERVICE_PRODUCT_TYPE,
    subtype: SERVICE_PLAN_SUBTYPE,
    cpt_code: values?.cpt_code?.id,
    description: values.description,
    specification: {}
  };
};

const formatRemote = (
  values: AddMasterProductFormValues
): MasterProductInput => {
  return {
    type: ACCESSORY_PRODUCT_TYPE,
    subtype: REMOTE_SUBTYPE,
    cpt_code: values?.cpt_code?.id,
    model: values.model,
    manufacturer: Number(values.manufacturer?.id),
    description: values.description,
    specification: {}
  };
};

const formatFee = (values: AddMasterProductFormValues): MasterProductInput => {
  return {
    type: FEE_PRODUCT_TYPE,
    description: values.description,
    specification: {}
  };
};

const formatSupply = (
  values: AddMasterProductFormValues
): MasterProductInput => {
  return {
    type: SUPPLY_PRODUCT_TYPE,
    specification: {},
    ...(values.description
      ? {
          description: values.description
        }
      : {
          manufacturer: values.manufacturer?.id,
          model: values.model
        })
  };
};

const formatOther = (
  values: AddMasterProductFormValues
): MasterProductInput => {
  const isService = Number(values.type) === SERVICE_PRODUCT_TYPE;
  return {
    type: Number(values.type),
    model: !isService ? values.model : undefined,
    subtype: Number(OTHER_SUBTYPE),
    manufacturer: !isService ? Number(values.manufacturer?.id) : undefined,
    cpt_code: values?.cpt_code?.id,
    description: values.description
  };
};
